import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Settings, Building2, Layers, GraduationCap, Info, AlertTriangle } from 'lucide-react';
import type { TuitionGridSettings, CampusInfo } from '@/types/tuition.types';

interface TuitionGridSettingsModalProps {
  settings: TuitionGridSettings | null;
  campusInfo: CampusInfo | null;
  isLoading: boolean;
  hasExistingGrids?: boolean; // Whether there are any configured grids
  onSave: (settings: Omit<TuitionGridSettings, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onClose: () => void;
}

const TuitionGridSettingsModal: React.FC<TuitionGridSettingsModalProps> = ({
  settings,
  campusInfo,
  isLoading,
  hasExistingGrids = false,
  onSave,
  onClose,
}) => {
  const [differsByNiveau, setDiffersByNiveau] = useState(false);
  const [differsByCampus, setDiffersByCampus] = useState(false);
  const [differsByTypeFormation, setDiffersByTypeFormation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const hasMultipleCampuses = campusInfo?.hasMultipleCampuses || false;

  // Initialize from existing settings
  useEffect(() => {
    if (settings) {
      setDiffersByNiveau(settings.differsByNiveau);
      setDiffersByCampus(settings.differsByCampus);
      setDiffersByTypeFormation(settings.differsByTypeFormation);
    }
  }, [settings]);

  // Check if settings have changed from the original
  const hasSettingsChanged = useMemo(() => {
    if (!settings) return true; // New settings, no change warning needed
    return (
      settings.differsByNiveau !== differsByNiveau ||
      settings.differsByCampus !== (hasMultipleCampuses ? differsByCampus : false) ||
      settings.differsByTypeFormation !== differsByTypeFormation
    );
  }, [settings, differsByNiveau, differsByCampus, differsByTypeFormation, hasMultipleCampuses]);

  // Check if the change will affect existing grids (dimensions changed)
  const willResetPrices = useMemo(() => {
    if (!settings || !hasExistingGrids) return false;
    // Warn if niveau, type formation, or campus settings change
    return (
      settings.differsByNiveau !== differsByNiveau ||
      settings.differsByTypeFormation !== differsByTypeFormation ||
      settings.differsByCampus !== (hasMultipleCampuses ? differsByCampus : false)
    );
  }, [settings, differsByNiveau, differsByTypeFormation, differsByCampus, hasExistingGrids, hasMultipleCampuses]);

  const handleSaveClick = () => {
    if (willResetPrices) {
      setShowConfirmation(true);
    } else {
      performSave();
    }
  };

  const performSave = async () => {
    setIsSaving(true);
    setShowConfirmation(false);
    try {
      await onSave({
        differsByNiveau,
        differsByCampus: hasMultipleCampuses ? differsByCampus : false,
        differsByTypeFormation,
      });
      onClose();
    } catch (err) {
      console.error('Error saving settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Paramètres de tarification
                  </h2>
                  <p className="text-sm text-gray-500">
                    Configurez comment les prix varient
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <>
                {/* Info banner */}
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Comment ça marche ?</p>
                    <p>
                      Ces paramètres déterminent si les prix sont les mêmes pour toutes les filières
                      ou s'ils varient selon certains critères. Si tous les paramètres sont "Non",
                      chaque filière aura un prix unique.
                    </p>
                  </div>
                </div>

                {/* Campus setting - only show if multiple campuses */}
                {hasMultipleCampuses && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-blue-900">
                              Prix différent par campus ?
                            </h3>
                            <p className="text-sm text-blue-600 mt-0.5">
                              {campusInfo?.campuses.length} campus disponibles
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={differsByCampus}
                              onChange={(e) => setDiffersByCampus(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                        {differsByCampus && (
                          <p className="text-xs text-blue-500 mt-2 bg-blue-100/50 px-2 py-1 rounded">
                            Vous pourrez définir des prix différents pour chaque campus
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Niveau setting */}
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Layers className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-purple-900">
                            Prix différent par niveau ?
                          </h3>
                          <p className="text-sm text-purple-600 mt-0.5">
                            1ère année, 2ème année, 3ème année, etc.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={differsByNiveau}
                            onChange={(e) => setDiffersByNiveau(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      {differsByNiveau && (
                        <p className="text-xs text-purple-500 mt-2 bg-purple-100/50 px-2 py-1 rounded">
                          Chaque niveau d'une filière pourra avoir un prix différent
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Type Formation setting */}
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-green-900">
                            Prix différent par type de formation ?
                          </h3>
                          <p className="text-sm text-green-600 mt-0.5">
                            Formation Initiale vs Formation Continue
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={differsByTypeFormation}
                            onChange={(e) => setDiffersByTypeFormation(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                      </div>
                      {differsByTypeFormation && (
                        <p className="text-xs text-green-500 mt-2 bg-green-100/50 px-2 py-1 rounded">
                          Formation Initiale et Continue auront des prix différents
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Récapitulatif</h4>
                  <p className="text-sm text-gray-600">
                    {!differsByNiveau && !differsByCampus && !differsByTypeFormation ? (
                      <>
                        Chaque <strong>filière parent</strong> aura un <strong>prix unique</strong> applicable à tous les niveaux
                        {hasMultipleCampuses && ', tous les campus'} et tous les types de formation.
                      </>
                    ) : (
                      <>
                        Les prix varieront selon:{' '}
                        {[
                          differsByNiveau && 'le niveau',
                          differsByCampus && 'le campus',
                          differsByTypeFormation && 'le type de formation',
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        . Vous pourrez configurer les prix avec des lignes extensibles.
                      </>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSaveClick}
              disabled={isSaving || isLoading || !hasSettingsChanged}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/60" onClick={() => setShowConfirmation(false)} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Réinitialisation des prix
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Changer ces paramètres va <strong>réinitialiser tous les prix existants</strong>.
                    Vous devrez reconfigurer les tarifs pour chaque filière après cette modification.
                  </p>
                  <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg mb-4">
                    Cette action est irréversible. Les prix actuellement configurés seront archivés.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => setShowConfirmation(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={performSave}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                      Confirmer et réinitialiser
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuitionGridSettingsModal;
